import { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Palette, Image as ImageIcon, Type, BarChart2, Music, Mic, Loader2, Wand2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const TTS_VOICES = ['alloy', 'ash', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer'];

export const defaultBranding = () => ({
  brandKitId: '',
  logo: { enabled: false, url: '', position: 'tr', size: 0.18, opacity: 0.9 },
  progressBar: { enabled: false, color: '#4f46e5' },
  lowerThird: { enabled: false, title: '', subtitle: '', start: 0, end: 5, color: '#4f46e5', textColor: '#ffffff' },
  outro: { enabled: false, mode: 'text', lines: ['', ''], url: '', duration: 3, bgColor: '#0f172a', textColor: '#ffffff' },
  music: { url: '', name: '', volume: 0.2 },
  duckMain: false,
  voiceover: { url: '', voice: 'alloy' },
});

const Row = ({ label, checked, onToggle, testid, children }) => (
  <div className="rounded-md border border-zinc-200 p-2.5">
    <label className="flex items-center gap-2 text-xs font-medium text-zinc-700 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onToggle(e.target.checked)} className="accent-indigo-600" data-testid={testid} />
      {label}
    </label>
    {checked && <div className="mt-2.5 space-y-2">{children}</div>}
  </div>
);

export const Phase4Panel = ({ value, onChange, api, scriptText }) => {
  const v = value;
  const [open, setOpen] = useState(false);
  const [kits, setKits] = useState([]);
  const [genVo, setGenVo] = useState(false);
  const logoInput = useRef(null);
  const outroInput = useRef(null);
  const musicInput = useRef(null);

  useEffect(() => {
    api.get('/brand-kits').then((r) => setKits(r.data.brand_kits || [])).catch(() => {});
  }, [api]);

  const upd = (partial) => onChange({ ...v, ...partial });
  const updG = (key, partial) => onChange({ ...v, [key]: { ...v[key], ...partial } });

  const applyKit = async (kitId) => {
    const kit = kits.find((k) => k.id === kitId);
    upd({ brandKitId: kitId });
    if (!kit) return;
    updG('progressBar', { color: kit.primary_color });
    updG('lowerThird', { color: kit.primary_color });
    updG('outro', { bgColor: kit.primary_color });
    if (kit.logo_url) {
      try {
        const abs = kit.logo_url.startsWith('http') ? kit.logo_url : `${process.env.REACT_APP_BACKEND_URL}${kit.logo_url}`;
        const r = await fetch(abs);
        const objUrl = URL.createObjectURL(await r.blob());
        updG('logo', { enabled: true, url: objUrl });
        toast.success(`Brand kit "${kit.name}" applied`);
      } catch (_) { toast.error('Could not load brand logo'); }
    }
  };

  const onFile = (e, key, extra = {}) => {
    const f = e.target.files?.[0];
    if (!f) return;
    updG(key, { enabled: true, url: URL.createObjectURL(f), ...extra });
  };

  const generateVoiceover = async () => {
    const text = (scriptText || '').trim();
    if (text.length < 2) { toast.error('Add a script (or captions) to voice first'); return; }
    setGenVo(true);
    try {
      const r = await api.post('/video/voiceover', { text: text.slice(0, 4000), voice: v.voiceover.voice }, { responseType: 'blob' });
      const objUrl = URL.createObjectURL(r.data);
      updG('voiceover', { url: objUrl });
      upd({ duckMain: true });
      toast.success('Voiceover ready — it will mix into your export');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate voiceover');
    } finally { setGenVo(false); }
  };

  return (
    <div className="rounded-lg border border-zinc-200 p-3 space-y-3" data-testid="phase4-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2" data-testid="phase4-toggle">
        <Label className="text-xs flex items-center gap-1.5 cursor-pointer">
          <Sparkles className="w-4 h-4 text-indigo-600" /> Branding, graphics & audio
        </Label>
        <span className="text-[11px] text-indigo-600">{open ? 'Hide' : 'Customize'}</span>
      </button>

      {open && (
        <div className="space-y-2.5">
          {/* Brand kit */}
          <div>
            <Label className="text-[11px] flex items-center gap-1"><Palette className="w-3.5 h-3.5" /> Brand kit</Label>
            <Select value={v.brandKitId || 'none'} onValueChange={(val) => applyKit(val === 'none' ? '' : val)}>
              <SelectTrigger data-testid="phase4-brandkit"><SelectValue placeholder="Manual (no kit)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Manual (no kit)</SelectItem>
                {kits.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-zinc-400 mt-1">Auto-fills logo + colors. You can still override anything below.</p>
          </div>

          {/* Logo */}
          <Row label={<span className="flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Logo / watermark</span>} checked={v.logo.enabled} onToggle={(c) => updG('logo', { enabled: c })} testid="phase4-logo-toggle">
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => logoInput.current?.click()} data-testid="phase4-logo-upload">Upload logo</Button>
              {v.logo.url && <span className="text-[10px] text-emerald-600">logo set</span>}
              <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e, 'logo')} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Position</Label>
                <Select value={v.logo.position} onValueChange={(val) => updG('logo', { position: val })}>
                  <SelectTrigger data-testid="phase4-logo-position"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tl">Top left</SelectItem>
                    <SelectItem value="tr">Top right</SelectItem>
                    <SelectItem value="bl">Bottom left</SelectItem>
                    <SelectItem value="br">Bottom right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px]">Size</Label>
                <input type="range" min="0.08" max="0.35" step="0.01" value={v.logo.size} onChange={(e) => updG('logo', { size: parseFloat(e.target.value) })} className="w-full accent-indigo-600" />
              </div>
            </div>
          </Row>

          {/* Progress bar */}
          <Row label={<span className="flex items-center gap-1"><BarChart2 className="w-3.5 h-3.5" /> Retention progress bar</span>} checked={v.progressBar.enabled} onToggle={(c) => updG('progressBar', { enabled: c })} testid="phase4-progress-toggle">
            <div className="flex items-center gap-2">
              <Label className="text-[10px]">Color</Label>
              <input type="color" value={v.progressBar.color} onChange={(e) => updG('progressBar', { color: e.target.value })} className="h-7 w-10 rounded border" data-testid="phase4-progress-color" />
            </div>
          </Row>

          {/* Lower third */}
          <Row label={<span className="flex items-center gap-1"><Type className="w-3.5 h-3.5" /> Lower-third title</span>} checked={v.lowerThird.enabled} onToggle={(c) => updG('lowerThird', { enabled: c })} testid="phase4-lowerthird-toggle">
            <Input value={v.lowerThird.title} onChange={(e) => updG('lowerThird', { title: e.target.value })} placeholder="Name / title" className="text-xs h-8" data-testid="phase4-lt-title" />
            <Input value={v.lowerThird.subtitle} onChange={(e) => updG('lowerThird', { subtitle: e.target.value })} placeholder="Subtitle (optional)" className="text-xs h-8" data-testid="phase4-lt-sub" />
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px]">Show from (s)</Label><Input type="number" min="0" step="0.5" value={v.lowerThird.start} onChange={(e) => updG('lowerThird', { start: parseFloat(e.target.value) || 0 })} className="text-xs h-8" /></div>
              <div><Label className="text-[10px]">Until (s)</Label><Input type="number" min="0" step="0.5" value={v.lowerThird.end} onChange={(e) => updG('lowerThird', { end: parseFloat(e.target.value) || 0 })} className="text-xs h-8" /></div>
            </div>
          </Row>

          {/* Outro */}
          <Row label={<span className="flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Outro / end card</span>} checked={v.outro.enabled} onToggle={(c) => updG('outro', { enabled: c })} testid="phase4-outro-toggle">
            <div className="flex gap-2">
              <Button size="sm" variant={v.outro.mode === 'text' ? 'default' : 'outline'} onClick={() => updG('outro', { mode: 'text' })} data-testid="phase4-outro-text-mode">Text card</Button>
              <Button size="sm" variant={v.outro.mode === 'image' ? 'default' : 'outline'} onClick={() => updG('outro', { mode: 'image' })} data-testid="phase4-outro-image-mode">Custom image</Button>
            </div>
            {v.outro.mode === 'text' ? (
              <>
                <Input value={v.outro.lines[0]} onChange={(e) => updG('outro', { lines: [e.target.value, v.outro.lines[1]] })} placeholder="Line 1 — e.g. Follow @handle" className="text-xs h-8" data-testid="phase4-outro-line1" />
                <Input value={v.outro.lines[1]} onChange={(e) => updG('outro', { lines: [v.outro.lines[0], e.target.value] })} placeholder="Line 2 — e.g. yoursite.com" className="text-xs h-8" data-testid="phase4-outro-line2" />
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => outroInput.current?.click()} data-testid="phase4-outro-image-upload">Upload end image</Button>
                {v.outro.url && <span className="text-[10px] text-emerald-600">image set</span>}
                <input ref={outroInput} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e, 'outro')} />
              </div>
            )}
            <div><Label className="text-[10px]">Duration (s)</Label><Input type="number" min="1" max="10" step="1" value={v.outro.duration} onChange={(e) => updG('outro', { duration: parseInt(e.target.value, 10) || 3 })} className="text-xs h-8 w-24" data-testid="phase4-outro-duration" /></div>
          </Row>

          {/* Music */}
          <Row label={<span className="flex items-center gap-1"><Music className="w-3.5 h-3.5" /> Background music</span>} checked={!!v.music.url} onToggle={(c) => { if (!c) updG('music', { url: '', name: '' }); else musicInput.current?.click(); }} testid="phase4-music-toggle">
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => musicInput.current?.click()} data-testid="phase4-music-upload">Upload track</Button>
              {v.music.name && <span className="text-[10px] text-emerald-600 truncate max-w-[120px]">{v.music.name}</span>}
              <input ref={musicInput} type="file" accept="audio/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) updG('music', { url: URL.createObjectURL(f), name: f.name }); }} />
            </div>
            <div><Label className="text-[10px]">Music volume</Label><input type="range" min="0" max="1" step="0.05" value={v.music.volume} onChange={(e) => updG('music', { volume: parseFloat(e.target.value) })} className="w-full accent-indigo-600" data-testid="phase4-music-volume" /></div>
            <label className="flex items-center gap-1.5 text-[11px] text-zinc-600 cursor-pointer">
              <input type="checkbox" checked={v.duckMain} onChange={(e) => upd({ duckMain: e.target.checked })} className="accent-indigo-600" data-testid="phase4-duck" />
              Lower the original audio (music-forward)
            </label>
          </Row>

          {/* Voiceover */}
          <div className="rounded-md border border-zinc-200 p-2.5 space-y-2">
            <Label className="text-xs font-medium text-zinc-700 flex items-center gap-1"><Mic className="w-3.5 h-3.5" /> AI voiceover {v.voiceover.url && <span className="text-[10px] text-emerald-600">· ready</span>}</Label>
            <div className="grid grid-cols-2 gap-2 items-end">
              <div>
                <Label className="text-[10px]">Voice</Label>
                <Select value={v.voiceover.voice} onValueChange={(val) => updG('voiceover', { voice: val })}>
                  <SelectTrigger data-testid="phase4-voice"><SelectValue /></SelectTrigger>
                  <SelectContent>{TTS_VOICES.map((vo) => <SelectItem key={vo} value={vo} className="capitalize">{vo}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button size="sm" variant="outline" onClick={generateVoiceover} disabled={genVo} data-testid="phase4-generate-voiceover">
                {genVo ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
                {genVo ? 'Voicing…' : 'Generate'}
              </Button>
            </div>
            <p className="text-[10px] text-zinc-400">Voices your teleprompter script (or captions) and mixes it over the video.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Phase4Panel;
